import { Check } from '@nt/ui/check'
import { ElementType } from 'react'

import { RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/lib/utils'

type CardVariant = 'company' | 'state'

interface CompactRadioCardProps {
  id: string
  value: string
  icon: ElementType
  title: string
  description?: string
  bulletPoints?: string[]
  /**
   * Kept so call sites need no change. It no longer picks a colour: a selected
   * option looks the same everywhere (ob-09 U11 — LLC green vs Wyoming blue
   * read as two different states).
   */
  variant?: CardVariant
  active?: boolean
  className?: string
  recommended?: boolean
}

/**
 * One selectable option in the onboarding wizard (a structure, a state): a
 * white card on the card ring. The chosen one is marked the way every chosen
 * card in the product is — a forest ring and the check disc — never by a
 * colour wash or a filled tile. The icon tile wears the service accent in force
 * (`data-service` on the wizard), selected or not.
 */
export function CompactRadioCard({
  id,
  value,
  icon: Icon,
  title,
  description,
  bulletPoints,
  className,
  active = false,
}: CompactRadioCardProps) {
  return (
    <div className="relative">
      <RadioGroupItem id={id} value={value} className="peer sr-only" />
      {/* A plain label: the whole card is the radio's label, and it carries the focus outline
          for the visually hidden radio before it. */}
      <label
        htmlFor={id}
        className={cn(
          'relative block w-full cursor-pointer rounded-card select-none peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring',
          className,
        )}
      >
        <div
          data-slot="compact-radio-card"
          data-selected={active ? 'true' : undefined}
          className={cn(
            'h-full rounded-card bg-card p-4 transition-shadow duration-150 ease-out md:p-5',
            active ? 'shadow-card ring-2 ring-primary' : 'shadow-card hover:ring-1 hover:ring-line',
          )}
        >
          {/* Selected: the check disc. Not selected: an empty ring in the control border. */}
          <div className="absolute top-4 right-4 md:top-5 md:right-5">
            {active ? (
              <Check size={20} />
            ) : (
              <span
                aria-hidden="true"
                data-slot="compact-radio-ring"
                className="block size-5 rounded-full border-2 border-input bg-card"
              />
            )}
          </div>

          <div className="space-y-3">
            {/* Header with icon tile and title */}
            <div className="flex items-center gap-3 pr-8">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-control bg-service-tint text-service">
                <Icon className="size-5" weight="duotone" aria-hidden="true" />
              </div>
              <h3 className="text-base font-semibold text-foreground">{title}</h3>
            </div>

            {/* Description and bullet points */}
            {(description || bulletPoints) && (
              <div className="space-y-2">
                {description && <p className="text-sm text-body">{description}</p>}
                {bulletPoints && (
                  <ul className="space-y-1.5">
                    {bulletPoints.map((point, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span
                          aria-hidden="true"
                          className="mt-2 inline-block size-1.5 shrink-0 rounded-full bg-muted-foreground/60"
                        />
                        <span className="text-sm text-body">{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </label>
    </div>
  )
}
